
/**
 * 输出SSML字符串
 * 
 * @param {array} records 语音配置对象数组，每个对象有如下属性
 * {
 *  name: "",
 *  style: "default",
 *  styledegree: 1,
 *  role: "default",
 *  rate: "default", //
 *  volume: "default",
 *  pitch: "default",
 *  text: "",
 * }
 * <voice name="$name">
 *   <mstts:express-as style="$style" styledegree="$styledegree" role="$role">
 *     <prosody pitch="$pitch" rate="$rate" volume="$volume">
 *       $text
 *     </prosody>
 *   </mstts:express-as>
 * </voice>
 */
function output(records) {
  let voices = [];
  for (let val of records) {
    let voice = outputVoice(val)
    mergeVoice(voices, voice)
  }
  return outputNodes(voices)
}

function outputNodes(nodes) {
  return nodes.map(n => outputNode(n)).join('')
}

function outputNode(node) {
  if (typeof node === 'string') {
    return node.replace(/([\u4e00-\u9fa5][a-z]+\d)+/g, (txt) => {
      let word = [], ph = [], ms
      const reg = /([\u4e00-\u9fa5])([a-z]+)(\d)/g
      while((ms = reg.exec(txt))) {
        word.push(ms[1]);
        ph.push(ms[2] + ' ' + (ms[3] === '0' ? '5' : ms[3]));
      }
      return `<phoneme alphabet="sapi" ph="${ph.join(' ')}">${word.join('')}</phoneme>`
    });
  }
  const attr = node.attr ? (' ' + node.attr) : ''
  const children = outputNodes(node.children)
  return `<${node.tag}${attr}>${children}</${node.tag}>`
}

function mergeVoice(nodes, node) {
  if (nodes.length > 0) {
    let prevNode = nodes[nodes.length - 1]
    if (typeof node === 'string' || typeof prevNode === 'string') {
      nodes.push(node)
    } else if (prevNode.tag === node.tag && prevNode.attr === node.attr) {
      for (let val of node.children) {
        mergeVoice(prevNode.children, val)
      }
    } else {
      nodes.push(node)
    }
  } else {
    nodes.push(node)
  }
}

function outputVoice(record) {
  let voice = {
    tag: 'voice',
    attr: `name="${record.name}"`,
    children: [],
  }
  //mstts:express-as
  let node = buildNode(voice, 'mstts:express-as', { style: record.style, styledegree: record.styledegree, role: record.role }, { styledegree: 1 });
  //prosody
  node = buildNode(node, 'prosody', { pitch: record.pitch, rate: record.rate, volume: record.volume });
  //text
  node.children.push(record.text)
  return voice
}

function buildNode(node, tagName, attrs, defAttrs) {
  attrs = buildAttrs(attrs, defAttrs)
  if (attrs.length > 0) {
    let el = {
      tag: tagName,
      attr: attrs.join(' '),
      children: [],
    }
    node.children.push(el)
    return el
  }
  return node
}

function buildAttrs(record, defVal) {
  let attrs = []
  if (defVal === undefined) {
    defVal = { default: 'default' }
  } else if (typeof defVal === 'object') {
    defVal = Object.assign({ default: 'default' }, defVal)
  } else {
    defVal = { default: defVal }
  }
  for (let key in record) {
    let dv = key in defVal ? defVal[key] : defVal.default
    if (record[key] !== dv && record[key] !== undefined && record[key] !== null && record[key] !== '') {
      attrs.push(`${key}="${record[key]}"`)
    }
  }
  return attrs
}

/**
 * 返回语音配置对象数组
 * @param {string} ssml 
 */
function parse(ssml) {
  ssml = cleanup(ssml || '')
  if (/^\s*$/.test(ssml)) {
    return [];
  }
  let parser = new DOMParser();
  let doc = parser.parseFromString(ssml, "text/xml");
  let result = parseNode(doc);
  if (!result) {
    return []
  }
  if (!Array.isArray(result)) {
    result = [result]
  }
  return result
}

const nameAttrsMap = {
  "voice": ['name'],
  "mstts:express-as": ['style', 'styledegree', 'role'],
  "prosody": ['pitch', 'rate', 'volume'],
}

function isTextNode(node) {
  return node.nodeType === 3 || node.nodeName === 'phoneme';
}

function parseNode(node) {
  if (node.nodeType === 3) {
    if (!node.nodeValue) {
      return null
    }
    const text = node.nodeValue.replace(/^\s+|\s+$/g, '')
    if (!text) {
      return null
    }
    return { text }
  }
  const nodeName = node.nodeName.toLowerCase();
  if (nodeName === 'parsererror') {
    return null;
  }
  if (nodeName === 'phoneme') {
    let alphabet = node.getAttribute('alphabet');
    if (alphabet === 'sapi') {
      let word = node.textContent.replace(/^\s+|\s+$/g, '')
      if (!word) {
        return null;
      }
      let ph = node.getAttribute('ph');
      let index = 0
      let text = ph.replace(/([a-z]+)\s*(\d)\s*/g, (phs, phWord, phPin) => {
        return word.substr(index++, 1) + phWord + phPin
      })
      return { text };
    }
  }
  let record = {};
  if (nameAttrsMap[nodeName]) {
    for (let attr of nameAttrsMap[nodeName]) {
      if (node.hasAttribute(attr)) {
        record[attr] = node.getAttribute(attr)
      }
    }
  }
  if (node.childNodes.length > 0) {
    let objs = [], prevNode;
    for (let childNode of node.childNodes) {
      let res = parseNode(childNode)
      if (res) {
        if (Array.isArray(res)) {
          objs = objs.concat(res.map(n => ({ ...record, ...n })))
        } else if(prevNode && isTextNode(prevNode) && isTextNode(childNode)) {
          objs[objs.length - 1].text += res.text
        } else {
          objs.push({ ...record, ...res })
        }
        prevNode = childNode
      }
    }
    if (objs.length > 0) {
      return objs.length === 1 ? objs[0] : objs;
    }
  }
  return record;
}

function indent(code, n) {
  n = n || 2
  let indentLevel = 0
  return code.replace(/\s*(<\/?)|(>)\s*/g, function(a, b, c) {
    let str = a
    if (b === '<') {
        str = "\n" + ''.padStart(indentLevel, ' ') + b
        indentLevel += n
    } else if(c === '>') {
        str = c + "\n" + ''.padStart(indentLevel, ' ')
    } else if(b === '</') {
        indentLevel -= n
        str = "\n" + ''.padStart(indentLevel, ' ') + b
    }
    return str
  }).replace(/\s+(\n *)/g, '$1').replace(/^\s+|\s+$/g, '')
}

function cleanup(code) {
  return code.replace(/(<!--.*?-->|<speak.*?>|<\/speak>)\s*/g, '') //移除所有注释和speak元素
    .replace(/Microsoft Server Speech Text to Speech Voice \(([\w-]+), (\w+)\)/g, '$1-$2') //转换微软后台的发音名称
    .replace(/\s+</g, '<') //移除所有 <标记前的空格
    .replace(/>\s+/g, '>') //移除所有 >标记后的空格
    .replace(/\s+/g, ' '); //将所有连续的空格替换成1个
}

export default {
  output,
  parse,
  indent,
  cleanup
}